import {
  type SpectralCalibration,
  type SpectralDopplerValues,
} from '@/models/spectralDoppler';

export function pixelToTimeSec(px: number, cal: SpectralCalibration): number {
  return (
    cal.referencePixelPhysicalValueX +
    (px - cal.referencePixelX0) * cal.physicalDeltaX
  );
}

/**
 * Pixel row → velocity in cm/s. physicalDeltaY is typically negative (pixel-Y
 * grows downward, velocity grows upward) — sign is preserved intentionally.
 */
export function pixelToVelocityCmS(
  py: number,
  cal: SpectralCalibration
): number {
  const raw =
    cal.referencePixelPhysicalValueY +
    (py - cal.referencePixelY0) * cal.physicalDeltaY;
  return cal.useAngleCorrection
    ? applyAngleCorrection(raw, cal.dopplerCorrectionAngle)
    : raw;
}

/**
 * Apply the Doppler angle correction. If angle is 0 this is a no-op.
 * v_corrected = v_raw / cos(angle) — only meaningful for non-zero angles.
 * Angle is in degrees (matching the backend's serialization).
 */
export function applyAngleCorrection(vCmS: number, angleDeg: number): number {
  if (angleDeg === 0) return vCmS;
  const c = Math.cos((angleDeg * Math.PI) / 180);
  if (c === 0) return vCmS;
  return vCmS / c;
}

/**
 * Re-apply a different correction angle to an already-corrected velocity.
 * Used when a reader toggles angle post-hoc (future v1.1 per-annotation toggle).
 * Angles are in degrees.
 */
export function reapplyAngleCorrection(
  correctedV: number,
  storedAngleDeg: number,
  newAngleDeg: number
): number {
  const cNew = Math.cos((newAngleDeg * Math.PI) / 180);
  if (cNew === 0) return correctedV;
  return (correctedV * Math.cos((storedAngleDeg * Math.PI) / 180)) / cNew;
}

/** Simplified Bernoulli: PG (mmHg) = 4 · v² (m/s). */
export function peakGradientMmHg(vCmS: number): number {
  const vMs = vCmS / 100;
  return 4 * vMs * vMs;
}

/** Mean gradient for a two-velocity segment: 4·(v1² + v1·v2 + v2²)/3 in mmHg with v in m/s. */
export function meanGradientSegmentMmHg(v1CmS: number, v2CmS: number): number {
  const v1 = v1CmS / 100;
  const v2 = v2CmS / 100;
  return (4 * (v1 * v1 + v1 * v2 + v2 * v2)) / 3;
}

/** Slope in cm/s² between two (time, velocity) samples. */
export function computeSlopeCmS2(
  v1CmS: number,
  v2CmS: number,
  deltaTSec: number
): number {
  if (deltaTSec === 0) return 0;
  return (v2CmS - v1CmS) / deltaTSec;
}

/**
 * Trapezoidal integration of |velocity(t)| dt over a polyline sampled in
 * physical units (seconds, cm/s). Returns VTI in cm.
 *
 * Samples should be in draw order; the area under the curve to the baseline
 * is computed by summing trapezoids between consecutive samples.
 */
export function integrateVTICm(
  samples: { timeSec: number; velocityCmS: number }[]
): { vtiCm: number; durationSec: number; peakVCmS: number; meanVCmS: number } {
  if (samples.length < 2) {
    return {
      vtiCm: 0,
      durationSec: 0,
      peakVCmS: samples[0]?.velocityCmS ?? 0,
      meanVCmS: samples[0]?.velocityCmS ?? 0,
    };
  }

  // Ensure time-ordered samples regardless of draw direction.
  const ordered = [...samples].sort((a, b) => a.timeSec - b.timeSec);

  // cm/s × s = cm; accumulated trapezoidal integral.
  let vtiAccumCm = 0;
  let peakAbs = 0;
  let peakSigned = 0;
  for (let i = 1; i < ordered.length; i += 1) {
    const prev = ordered[i - 1];
    const curr = ordered[i];
    const dt = curr.timeSec - prev.timeSec;
    if (dt > 0) {
      const v1 = Math.abs(prev.velocityCmS);
      const v2 = Math.abs(curr.velocityCmS);
      vtiAccumCm += ((v1 + v2) / 2) * dt;

      const absCurr = Math.abs(curr.velocityCmS);
      if (absCurr > peakAbs) {
        peakAbs = absCurr;
        peakSigned = curr.velocityCmS;
      }
      const absPrev = Math.abs(prev.velocityCmS);
      if (i === 1 && absPrev > peakAbs) {
        peakAbs = absPrev;
        peakSigned = prev.velocityCmS;
      }
    }
  }

  const durationSec = ordered[ordered.length - 1].timeSec - ordered[0].timeSec;
  const meanVCmS = durationSec > 0 ? vtiAccumCm / durationSec : 0;

  return {
    vtiCm: vtiAccumCm,
    durationSec,
    peakVCmS: peakSigned,
    meanVCmS,
  };
}

/** True when (px, py) falls inside the spectral region's bounding box. */
export function isPointInRegionBbox(
  px: number,
  py: number,
  cal: Pick<
    SpectralCalibration,
    'bboxMinX' | 'bboxMinY' | 'bboxMaxX' | 'bboxMaxY'
  >
): boolean {
  return (
    px >= cal.bboxMinX &&
    px <= cal.bboxMaxX &&
    py >= cal.bboxMinY &&
    py <= cal.bboxMaxY
  );
}

/** Clamp an image-pixel point to the region bbox (inclusive of edges). */
export function clampToRegionBbox(
  px: number,
  py: number,
  cal: Pick<
    SpectralCalibration,
    'bboxMinX' | 'bboxMinY' | 'bboxMaxX' | 'bboxMaxY'
  >
): [number, number] {
  return [
    Math.min(Math.max(px, cal.bboxMinX), cal.bboxMaxX),
    Math.min(Math.max(py, cal.bboxMinY), cal.bboxMaxY),
  ];
}

/**
 * Derive values for a Velocity Probe (single point).
 * Angle correction is baked into pixelToVelocityCmS.
 */
export function deriveProbeValues(
  px: number,
  py: number,
  cal: SpectralCalibration
): SpectralDopplerValues {
  const timeSec = pixelToTimeSec(px, cal);
  const velocityCmS = pixelToVelocityCmS(py, cal);
  return {
    timeMs: timeSec * 1000,
    peakV_cmS: velocityCmS,
    peakGradient_mmHg: peakGradientMmHg(Math.abs(velocityCmS)),
  };
}

/** Derive values for a Velocity Segment (two points). */
export function deriveSegmentValues(
  p1: [number, number],
  p2: [number, number],
  cal: SpectralCalibration
): SpectralDopplerValues {
  const t1 = pixelToTimeSec(p1[0], cal);
  const t2 = pixelToTimeSec(p2[0], cal);
  const v1 = pixelToVelocityCmS(p1[1], cal);
  const v2 = pixelToVelocityCmS(p2[1], cal);
  const deltaT = t2 - t1;
  const deltaV = v2 - v1;
  const slope = computeSlopeCmS2(v1, v2, deltaT);
  const absPeak = Math.max(Math.abs(v1), Math.abs(v2));
  return {
    v1_cmS: v1,
    v2_cmS: v2,
    deltaV_cmS: deltaV,
    deltaT_sec: deltaT,
    slope_cmS2: slope,
    peakV_cmS: Math.abs(v1) >= Math.abs(v2) ? v1 : v2,
    meanGradient_mmHg: meanGradientSegmentMmHg(Math.abs(v1), Math.abs(v2)),
    peakGradient_mmHg: peakGradientMmHg(absPeak),
  };
}

/** Derive values for a VTI trace (polyline of image-pixel points). */
export function deriveVTIValues(
  points: [number, number][],
  cal: SpectralCalibration
): SpectralDopplerValues {
  if (points.length < 2) {
    return {};
  }
  const samples = points.map(([px, py]) => ({
    timeSec: pixelToTimeSec(px, cal),
    velocityCmS: pixelToVelocityCmS(py, cal),
  }));
  const { vtiCm, durationSec, peakVCmS, meanVCmS } = integrateVTICm(samples);
  return {
    vti_cm: vtiCm,
    durationMs: durationSec * 1000,
    peakV_cmS: peakVCmS,
    meanV_cmS: meanVCmS,
    peakGradient_mmHg: peakGradientMmHg(Math.abs(peakVCmS)),
    meanGradient_mmHg: peakGradientMmHg(Math.abs(meanVCmS)),
  };
}
